import { apiClient } from "@/lib/api";
import i18n from "@/i18n";
import {
  ALL_WEEKDAYS,
  type ScheduleResponse,
  type WeeklySchedule,
  type ScheduleOverride,
  type ScheduleOverrideCreate,
} from "@/types";

export const scheduleService = {
  // Get full schedule (weekly + overrides)
  getSchedule: async (activityId: string): Promise<ScheduleResponse> => {
    const response = await apiClient.get<ScheduleResponse>(
      `/activities/${activityId}/schedule`,
    );
    return response.data;
  },

  // Update weekly schedule (partial update)
  updateWeeklySchedule: async (
    activityId: string,
    data: Partial<WeeklySchedule>,
  ): Promise<WeeklySchedule> => {
    const response = await apiClient.put<WeeklySchedule>(
      `/activities/${activityId}/schedule/weekly`,
      data,
    );
    return response.data;
  },

  // Create a schedule override
  createOverride: async (
    activityId: string,
    data: ScheduleOverrideCreate,
  ): Promise<ScheduleOverride> => {
    const response = await apiClient.post<ScheduleOverride>(
      `/activities/${activityId}/schedule/overrides`,
      data,
    );
    return response.data;
  },

  // Update a schedule override
  updateOverride: async (
    activityId: string,
    overrideId: string,
    data: Partial<ScheduleOverrideCreate>,
  ): Promise<ScheduleOverride> => {
    const response = await apiClient.put<ScheduleOverride>(
      `/activities/${activityId}/schedule/overrides/${overrideId}`,
      data,
    );
    return response.data;
  },

  // Delete a schedule override
  deleteOverride: async (
    activityId: string,
    overrideId: string,
  ): Promise<void> => {
    await apiClient.delete(
      `/activities/${activityId}/schedule/overrides/${overrideId}`,
    );
  },
};

// Helper functions — re-export under the schedule-local name for call sites.
export { hhmmFromTimeString as formatTime } from "@/lib/datetime";

const WEEKDAY_KEYS: ReadonlySet<string> = new Set(ALL_WEEKDAYS);

export function getWeekdayLabel(day: string): string {
  if (!WEEKDAY_KEYS.has(day)) return day;
  return i18n.t(`weekdays.${day}`, { ns: "activities" });
}

export function createEmptyWeeklySchedule(): WeeklySchedule {
  const empty = {} as WeeklySchedule;
  for (const day of ALL_WEEKDAYS) {
    empty[day] = [];
  }
  return empty;
}
